/* eslint-disable react-hooks/exhaustive-deps */
import {
  AnalyticMetricEnum,
  AnalyticMetricRecord,
  Company,
  getMetricCallback,
  Metric,
  MetricClass,
  MetricDimension,
} from '@laborability/commons';
import { ANALYTIC_METRIC_LABEL } from './types';
import { COLORS, LBTProgressSpinner } from '@laborability/components';
import { useEffect, useState } from 'react';
import { useRecoilCallback } from 'recoil';
import dayjs from 'dayjs';

interface Props {
  currentMetric: AnalyticMetricEnum;
  borderColor: string;
  companyId: number | null;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  comparisonStartDate: dayjs.Dayjs | null;
  comparisonEndDate: dayjs.Dayjs | null;
  companies?: Company[];
}

export default function Panel({
  borderColor,
  currentMetric,
  companyId,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  companies,
}: Props) {
  const getMetric = useRecoilCallback(getMetricCallback, []);
  const [metricData, setMetricData] = useState<Metric>(new MetricClass());
  const [metricLoader, setMetricLoader] = useState<number>(0);
  const [comparisonMetricData, setComparisonMetricData] =
    useState<Metric | null>(null);

  const getMetricData = async () => {
    if (!companyId && !companies?.length) return;
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setMetricData(res.data);
  };

  const getComparisonMetricData = async () => {
    if (!companyId && !companies?.length) return;
    if (!comparisonStartDate || !comparisonEndDate)
      return setComparisonMetricData(null);
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: comparisonStartDate.format('YYYY-MM-DD'),
      end_date: comparisonEndDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setComparisonMetricData(res.data);
  };

  const calculateAverage = (data: { date: string; value: number }[]) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const average = total / data.length;
    return average % 1 === 0 ? average.toString() : average.toFixed(2);
  };

  const calculateTotal = (data: { date: string; value: number }[]) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (isNaN(total)) {
      return '0';
    }
    return total % 1 === 0 ? total.toString() : total.toFixed(2);
  };

  const calculatePercentageDifference = (current: number, previous: number) => {
    if (!previous) return null;
    const difference = ((current - previous) / previous) * 100;
    return difference.toFixed(0);
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}'${minutes}''`;
  };

  const getPercentageDifference = (
    dimension: MetricDimension,
    value: number,
    getData: (
      data: {
        date: string;
        value: number;
      }[],
    ) => string | number,
  ) => {
    const comparision = comparisonMetricData?.dimensions.find(
      item => item.name === dimension.name,
    );
    const averageComparison = parseFloat(
      getData(comparision?.data ?? []) as string,
    );
    return calculatePercentageDifference(value, averageComparison) ?? '0';
  };

  const getColor = (value: number) => {
    if (value > 0) {
      return COLORS.getInstance().SUCCESS_MAIN;
    } else if (value < 0) {
      return COLORS.getInstance().ERROR_MAIN;
    } else {
      return 'black';
    }
  };

  const getIcon = (value: number) => {
    if (value > 0) {
      return '↑';
    } else if (value < 0) {
      return '↓';
    } else {
      return '';
    }
  };

  const getNumericalDifference = (
    dimension: MetricDimension,
    value: number,
    getData: (
      data: {
        date: string;
        value: number;
      }[],
    ) => string | number,
  ) => {
    const comparision = comparisonMetricData?.dimensions.find(
      item => item.name === dimension.name,
    );
    const averageComparison = parseFloat(
      getData(comparision?.data ?? []) as string,
    );
    return (value - averageComparison) % 1 === 0
      ? (value - averageComparison).toString()
      : (value - averageComparison).toFixed(2);
  };

  useEffect(() => {
    getMetricData();
  }, [startDate, endDate, companyId, JSON.stringify(companies)]);

  useEffect(() => {
    getComparisonMetricData();
  }, [comparisonStartDate, comparisonEndDate, companyId]);

  if (!!metricLoader) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <LBTProgressSpinner />
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '32px',
        border: `2px solid ${borderColor}`,
        borderRadius: '5px',
        padding: '5px 15px',
      }}
    >
      <h1
        style={{
          textAlign: 'center',
          margin: '10px',
          fontSize: '1rem',
          fontWeight: 'normal',
        }}
      >
        {ANALYTIC_METRIC_LABEL[metricData.metric]}
      </h1>
      {metricData.dimensions.length === 1 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontSize: '1.5rem',
            flexDirection: 'column',
          }}
        >
          {metricData.dimensions.map(dimension => {
            let value: number | string = 0;
            let percentageDifference = null;
            let numericalDifference = null;
            if (AnalyticMetricRecord[currentMetric].total) {
              value = calculateTotal(dimension.data);
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(value),
                  calculateTotal,
                );
                numericalDifference = getNumericalDifference(
                  dimension,
                  parseFloat(value),
                  calculateTotal,
                );
              }
            }
            if (AnalyticMetricRecord[currentMetric].percentage) {
              value = `${calculateAverage(dimension.data)}%`;
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(calculateAverage(dimension.data)),
                  calculateAverage,
                );
                numericalDifference = getNumericalDifference(
                  dimension,
                  parseFloat(calculateAverage(dimension.data)),
                  calculateAverage,
                );
              }
            }
            if (AnalyticMetricRecord[currentMetric].average) {
              value = calculateAverage(dimension.data);
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(value),
                  calculateAverage,
                );
                numericalDifference = getNumericalDifference(
                  dimension,
                  parseFloat(calculateAverage(dimension.data)),
                  calculateAverage,
                );
              }
            }

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  alignItems: 'center',
                }}
                key={dimension.name}
              >
                <div
                  style={{
                    margin: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <p
                    style={{
                      fontSize: '2rem',
                      fontWeight: 'bold',
                      margin: '0px',
                    }}
                  >
                    {AnalyticMetricRecord[currentMetric].time
                      ? formatTime(parseFloat(value as string))
                      : value}
                  </p>
                  {dimension.name && (
                    <p
                      style={{
                        margin: '0px',
                        fontSize: '0.7rem',
                        fontWeight: 'thin',
                      }}
                    >
                      {dimension.name}
                    </p>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {percentageDifference !== null && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        margin: '10px, 0px',
                        flexDirection: 'row',
                        gap: '5px',
                        color: getColor(parseFloat(percentageDifference)),
                      }}
                    >
                      <p>{getIcon(parseFloat(percentageDifference))}</p>
                      <p>{percentageDifference}%</p>
                    </div>
                  )}
                  {numericalDifference !== null && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1rem',
                        margin: '10px, 0px',
                        color: getColor(parseFloat(numericalDifference)),
                      }}
                    >
                      {parseFloat(numericalDifference) > 0 ? (
                        <p>
                          +
                          {AnalyticMetricRecord[currentMetric].time
                            ? formatTime(parseFloat(numericalDifference))
                            : numericalDifference}
                          {AnalyticMetricRecord[currentMetric].percentage &&
                            '%'}
                        </p>
                      ) : (
                        <p>
                          {AnalyticMetricRecord[currentMetric].time
                            ? formatTime(parseFloat(numericalDifference))
                            : numericalDifference}
                          {AnalyticMetricRecord[currentMetric].percentage &&
                            '%'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            fontSize: '1.5rem',
            flexDirection: 'row',
          }}
        >
          {metricData.dimensions.map(dimension => {
            let value: number | string = 0;
            let percentageDifference = null;
            if (AnalyticMetricRecord[currentMetric].total) {
              value = calculateTotal(dimension.data);
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(value),
                  calculateTotal,
                );
              }
            }
            if (AnalyticMetricRecord[currentMetric].percentage) {
              value = `${calculateAverage(dimension.data)}%`;
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(calculateAverage(dimension.data)),
                  calculateAverage,
                );
              }
            }
            if (AnalyticMetricRecord[currentMetric].average) {
              value = calculateAverage(dimension.data);
              if (comparisonMetricData) {
                percentageDifference = getPercentageDifference(
                  dimension,
                  parseFloat(value),
                  calculateAverage,
                );
              }
            }

            return (
              <div
                style={{
                  margin: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <p
                  style={{
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    margin: '0px',
                  }}
                >
                  {AnalyticMetricRecord[currentMetric].time
                    ? formatTime(parseFloat(value as string))
                    : value}
                </p>
                {dimension.name && (
                  <p
                    style={{
                      margin: '0px',
                      fontSize: '0.7rem',
                      fontWeight: 'thin',
                    }}
                  >
                    {dimension.name}
                  </p>
                )}
                {percentageDifference && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '1rem',
                      margin: '10px, 0px',
                      flexDirection: 'row',
                      gap: '5px',
                      color: getColor(parseFloat(percentageDifference)),
                    }}
                  >
                    <p>{getIcon(parseFloat(percentageDifference))}</p>
                    <p>{percentageDifference}%</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
